import { useId } from 'react';
import classNames from 'classnames';
import Tooltip from '../../tooltip';
import { Link } from 'src/navigation';
import { socialLinks } from './footer-data';
import { useQRCode } from 'next-qrcode';
import styles from './global-footer.module.scss';

// We render two separate versions of QR code links to keep things accessible (e.g. no a tags that don't link out).
// Social links with QR Codes should only open the Tooltip with the QR code while on desktop viewport widths.
// When on mobile, treat the button as a normal link.
export const SocialLinks = () => {
	return (
		<ul className={styles.socialLinks}>
			{socialLinks
				.map((socialLink) => {
					const hasQRCode = !!socialLink.hasQrCode;
					const links = [
						<li
							key={
								!hasQRCode
									? socialLink.siteName
									: `${socialLink.siteName}-mobile`
							}
							className={classNames(styles.socialItem, {
								[styles.mobile]: hasQRCode,
							})}
						>
							<SocialLink socialLink={socialLink} />
						</li>,
					];

					if (hasQRCode) {
						links.push(
							<li
								key={`${socialLink.siteName}-desktop`}
								className={classNames(styles.socialItem, {
									[styles.desktop]: hasQRCode,
								})}
							>
								<SocialButtonWithQRCode socialLink={socialLink} />
							</li>,
						);
					}

					return links;
				})
				.flat()}
		</ul>
	);
};

const SocialButtonWithQRCode = ({
	socialLink,
}: {
	socialLink: (typeof socialLinks)[0];
}) => {
	const id = useId();
	const { SVG } = useQRCode();
	const jsxDescription = (
		<div
			className={styles.tooltipContentContainer}
			data-testid="social-qr-code-tooltip"
		>
			<SVG
				text={socialLink.url}
				options={{
					margin: 0,
					color: {
						dark: '#000',
						light: '#FFF',
					},
				}}
			/>
			{socialLink.qrContentText}
		</div>
	);

	return (
		<Tooltip jsxDescription={jsxDescription} id={id} clickToOpen={true}>
			<button
				title={socialLink.title}
				className={classNames(styles.socialLink, styles.socialButton)}
			>
				{socialLink.icon}
			</button>
		</Tooltip>
	);
};

const SocialLink = ({
	socialLink,
}: {
	socialLink: (typeof socialLinks)[0];
}) => {
	return (
		<Link
			prefetch={false}
			title={socialLink.title}
			href={socialLink.url}
			target="_blank"
			className={styles.socialLink}
			rel="noreferrer"
		>
			{socialLink.icon}
		</Link>
	);
};
